import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AlertTriangle, CheckCircle2, Gavel, Loader2, XCircle } from 'lucide-react'
import { formatDate, formatDateTime } from '@/lib/utils/format'
import { useDecide, useDecision } from '../hooks/useSupport'
import type { DecidePayload, DuplicateAccountDecision, HeldClosureDecision, RefundDecision } from '../api/supportApi'
import { printStatus, refundApproveText } from '../utils/decision'

interface DecisionCardProps {
  ticketId: string
  /** Only an open ticket can be decided. */
  open: boolean
}

/**
 * Approve or decline what the agent could not decide on its own (a refund, which of two accounts
 * an artist keeps, a closure held by the daily cap). Approving does it; either way a notification
 * is drafted into the reply box, and sending it resolves the ticket.
 */
export function DecisionCard({ ticketId, open }: DecisionCardProps) {
  const { data, isLoading } = useDecision(ticketId)
  const decide = useDecide()
  const [note, setNote] = useState('')
  const [keepUserId, setKeepUserId] = useState<number | null>(null)
  const [confirming, setConfirming] = useState<DecidePayload['outcome'] | null>(null)

  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (!data || data.type === 'none') return null

  if ('decided' in data) {
    const d = data.decided
    return (
      <Card className="border-slate-200">
        <CardContent className="py-3 px-4 text-sm flex flex-wrap items-center gap-2">
          {d.outcome === 'approved' ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-slate-500" />}
          <span className="font-medium capitalize">{d.outcome.replace('_', ' ')}</span>
          <span className="text-muted-foreground">
            by {d.by || 'a manager'} on {formatDateTime(d.at)}
            {d.notifiedAt ? ' · customer notified' : ' · notification not sent yet'}
          </span>
        </CardContent>
      </Card>
    )
  }
  if (!open) return null

  // Neither 'none' nor decided (both returned above).
  const pending = data as RefundDecision | DuplicateAccountDecision | HeldClosureDecision
  const refund = data.type === 'refund' ? (data as RefundDecision) : null
  const duplicate = data.type === 'duplicate_account' ? (data as DuplicateAccountDecision) : null
  const held = data.type === 'held_closure' ? (data as HeldClosureDecision) : null
  const keep = keepUserId ?? duplicate?.suggestedKeepUserId ?? null
  const keptAccount = duplicate?.accounts.find((a) => a.id === keep)
  const closing = duplicate?.accounts.filter((a) => a.id !== keep) ?? []
  const closingLive = closing.some((a) => a.live > 0)

  const approveText = refund
    ? refundApproveText(refund)
    : held
    ? {
        button: 'Approve: close this account',
        confirm: `Close "${held.account?.name}" (${held.account?.login}): artworks off sale, artist page removed, sign-in and emails stopped.`,
        warning: held.account?.live ? `${held.account.live} live artworks will be taken off sale.` : undefined,
      }
    : {
        button: 'Approve: keep this account',
        confirm: `Keep "${keptAccount?.name}" (${keptAccount?.login}) and close ${closing
          .map((a) => `"${a.name}" (${a.login})`)
          .join(', ')}: artworks off sale, artist page removed, sign-in and emails stopped.`,
        warning: closingLive ? 'An account to close has live artworks; move them first.' : undefined,
      }

  const run = (outcome: DecidePayload['outcome']) => {
    decide.mutate(
      {
        ticketId,
        payload: {
          outcome,
          note: note.trim() || undefined,
          keepUserId: keep ?? undefined,
          refundEvenIfPrinting: refund?.plan === 'printing' ? true : undefined,
        },
      },
      { onSettled: () => setConfirming(null) }
    )
  }

  return (
    <Card className="border-violet-200 bg-violet-50/40 dark:bg-violet-950/10 dark:border-violet-900">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Gavel className="h-4 w-4 text-violet-600" />
          {refund ? 'Refund request' : held ? 'Account closure held' : 'Duplicate artist accounts'}
        </CardTitle>
        <CardDescription>{pending.summary}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {refund?.order && (
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Order</dt>
            <dd className="font-medium">{refund.order.name} · {refund.order.total} {refund.order.currency}</dd>
            <dt className="text-muted-foreground">Placed</dt>
            <dd>{formatDateTime(refund.order.placedAt)}</dd>
            <dt className="text-muted-foreground">Items</dt>
            <dd>{refund.order.items.join('; ')}</dd>
            <dt className="text-muted-foreground">Shopify</dt>
            <dd className="capitalize">{refund.order.financialStatus} · {refund.order.fulfillmentStatus}</dd>
            <dt className="text-muted-foreground">Print</dt>
            <dd>{printStatus(refund)}</dd>
          </dl>
        )}

        {held && (
          <div className="space-y-2 text-sm">
            {held.account && (
              <p>
                <span className="font-medium">{held.account.name || 'No artist name'}</span>{' '}
                <span className="text-muted-foreground">
                  #{held.account.id} · {held.account.login} · {held.account.live} live, {held.account.artworks} not disabled
                </span>
              </p>
            )}
            <p className="text-muted-foreground">Closed in the last 24 hours:</p>
            <ul className="list-disc pl-5 text-muted-foreground">
              {held.closuresInLastDay.map((c) => (
                <li key={`${c.userId}-${c.at}`}>
                  {c.name || 'No artist name'} (#{c.userId}), {c.how}, {formatDateTime(c.at)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {duplicate && (
          <div className="space-y-2" role="radiogroup" aria-label="Account to keep">
            {duplicate.accounts.map((a) => (
              <label
                key={a.id}
                className={`flex items-start gap-3 rounded-md border p-3 text-sm cursor-pointer ${
                  a.id === keep ? 'border-violet-400 bg-white dark:bg-transparent' : 'border-border'
                }`}
              >
                <input
                  type="radio"
                  name={`keep-${ticketId}`}
                  className="mt-1"
                  checked={a.id === keep}
                  onChange={() => setKeepUserId(a.id)}
                />
                <span className="space-y-0.5">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{a.name || 'No artist name'}</span>
                    <span className="text-muted-foreground">#{a.id}</span>
                    {a.wroteFrom && <Badge variant="outline" className="text-xs">wrote in</Badge>}
                    {a.id === keep ? (
                      <Badge className="text-xs">keep</Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs">close</Badge>
                    )}
                  </span>
                  <span className="block text-muted-foreground">
                    {a.login} · created {formatDate(a.created)}
                    {a.lastLogin ? ` · last signed in ${formatDate(a.lastLogin)}` : ' · never signed in'}
                  </span>
                  <span className="block text-muted-foreground">
                    {a.live} live, {a.artworks} not disabled
                    {a.handle ? ` · myarthaus.com/collections/${a.handle}` : ''}
                  </span>
                </span>
              </label>
            ))}
            {duplicate.requestedName && (
              <p className="text-xs text-muted-foreground">They asked to be called “{duplicate.requestedName}”.</p>
            )}
          </div>
        )}

        {approveText.warning && (
          <p className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" /> {approveText.warning}
          </p>
        )}

        <Textarea
          placeholder="Note for the notification (optional), e.g. a reason when declining"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={!pending.canApprove || closingLive || decide.isPending}
            onClick={() => setConfirming('approved')}
          >
            {decide.isPending && confirming === 'approved' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {approveText.button}
          </Button>
          <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => setConfirming('declined')}>
            Decline
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Either way, a notification for the customer is drafted in the reply box below. Sending it resolves the ticket.
        </p>
      </CardContent>

      <AlertDialog open={confirming !== null} onOpenChange={(o) => !o && !decide.isPending && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirming === 'approved' ? approveText.button : 'Decline this request?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === 'approved'
                ? `${approveText.confirm} This cannot be undone from here.`
                : held
                ? 'The account stays open and the artist\'s link no longer works. A notification telling them is drafted for you to send.'
                : 'Nothing is changed. A notification telling the customer is drafted for you to send.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={decide.isPending}>Back</AlertDialogCancel>
            <AlertDialogAction
              disabled={decide.isPending}
              onClick={(e) => {
                // Keep the dialog open until the work is done, so the result is seen.
                e.preventDefault()
                if (confirming) run(confirming)
              }}
            >
              {decide.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {confirming === 'approved' ? 'Yes, do it' : 'Decline'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

